import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react';
import { Children, Fragment, useMemo } from 'react';

import { breadcrumbs } from '~/chords/Breadcrumbs/utils/variants';
import { Icon } from '~/chords/Icon';
import { cn } from '~/lib/cn';
import { useBrand } from '~/modules/branding/hooks/useBrand';

export interface BreadcrumbsRootProps
  extends Omit<ComponentPropsWithoutRef<'nav'>, 'className'> {
  children: NonNullable<ReactNode>;
}

export function BreadcrumbsRoot({
  children,
  ...delegatedProps
}: BreadcrumbsRootProps) {
  const brand = useBrand();
  const { root } = breadcrumbs({
    variant: brand.name,
  });

  const childrenWithSeparators = useMemo(() => {
    const validChildren = Children.toArray(children).filter(
      Boolean,
    ) as ReactElement[];

    return validChildren.map((child, index) => {
      return (
        <Fragment key={child.key}>
          {index ? (
            <Icon
              type="chevron-right"
              size={brand.name === 'ADA' ? 'xs' : 'md'}
              role="presentation"
              aria-hidden
            />
          ) : null}
          {child}
        </Fragment>
      );
    });
  }, [children, brand.name]);

  if (!childrenWithSeparators?.length) {
    return null;
  }

  return (
    <nav {...delegatedProps} className={cn(root())}>
      {childrenWithSeparators}
    </nav>
  );
}

BreadcrumbsRoot.displayName = 'Breadcrumbs.Root';
